"use client";

import React, { useEffect, useState, useCallback, type MutableRefObject } from "react";
import { motion } from "framer-motion";
import { FastForward, CandlestickChart, MousePointerClick } from "lucide-react";
import { useFastForward } from "@/contexts/FastForwardContext";
import type { TradingChartRef } from "@/components/TradingChart/TradingChart";
import { FastForwardLoading } from "./loading";
import { FastForwardSuccess } from "./success";
import { getViewModeChrome } from "../view-mode-chrome";

type LocalExecutionState = 'idle' | 'executed';

interface FastForwardButtonProps {
  chartRef?: MutableRefObject<TradingChartRef | null>;
  sessionId?: string;
  toolCallId?: string;
  args?: { time?: number };
  viewMode?: string;
}

export function FastForwardButton({ chartRef, sessionId, toolCallId, args, viewMode}: FastForwardButtonProps) {
  const chrome = getViewModeChrome(viewMode);
  const { 
    state: globalState, 
    progress, 
    error, 
    startFastForward, 
    setChartRef, 
    setDemoActionData, 
    setVisible, 
    resetStateForNewTool,
    markToolCallExecuted,
    isToolCallExecuted,
    setToolCallTimeArg
  } = useFastForward();
  const [countdown, setCountdown] = useState(60);
  
  // Set chart ref and demo action data in context when component mounts
  useEffect(() => {
    if (chartRef) {
      setChartRef(chartRef);
    }
  }, [chartRef, setChartRef]);

  useEffect(() => {
    if (sessionId && toolCallId) {
      setDemoActionData(sessionId, toolCallId);
      // Reset global state for new tool call (but don't affect individual tool displays)
      resetStateForNewTool();
    }
  }, [sessionId, toolCallId, setDemoActionData, resetStateForNewTool]);

  // Store time argument from args when component mounts or args change
  useEffect(() => {
    if (toolCallId && args?.time !== undefined) {
      setToolCallTimeArg(toolCallId, args.time);
    }
  }, [toolCallId, args?.time, setToolCallTimeArg]);

  // Set visible when this component mounts (fast_forward tool display appears)
  useEffect(() => {
    setVisible(true);
    // Don't reset global state here - it affects all other tool displays
  }, [setVisible, toolCallId, isToolCallExecuted]);

  // Remove this useEffect - execution marking is now handled in the context's updateState
  // This prevents new tool displays from incorrectly marking themselves as executed

  const handleFastForward = useCallback(async () => {
    await startFastForward();
  }, [startFastForward]);

  // Determine what to display based on toolCallId execution state and global state
  const isThisToolExecuted = toolCallId ? isToolCallExecuted(toolCallId) : false;
  const isGloballyLoading = globalState === 'loading' || globalState === 'streaming';
  
  // Only show loading if this tool hasn't been executed yet AND there's a global operation
  const shouldShowLoading = !isThisToolExecuted && isGloballyLoading;
  const shouldShowSuccess = isThisToolExecuted;
  const shouldShowError = !isThisToolExecuted && globalState === 'error';
  const shouldShowButton = !isThisToolExecuted && !isGloballyLoading && globalState !== 'error';

  // Countdown timer effect
  useEffect(() => {
    if (!shouldShowButton) {
      setCountdown(60); // Reset countdown when not showing button
      return;
    }

    if (countdown <= 0) {
      handleFastForward(); // Auto-trigger when countdown reaches 0
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [shouldShowButton, countdown, handleFastForward]);

  // Show loading state (during active execution)
  if (shouldShowLoading) {
    return (
      <FastForwardLoading 
        progress={progress} 
        state={globalState}
        viewMode={viewMode}
      />
    );
  }

  // Show success state (this instance was executed)
  if (shouldShowSuccess) {
    return <FastForwardSuccess barsAdded={0} viewMode={viewMode} />;
  }

  // Show error state (fallback to button with error styling)
  if (shouldShowError) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="w-full"
      >
        <div className={`rounded-md border border-red-400/30 ${chrome.card} overflow-hidden`}>
          <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
            <div className="flex items-center gap-2.5">
              <CandlestickChart
                  className="w-4 h-4 text-red-400"
                  strokeWidth={2}
                />
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${chrome.text} leading-tight`}>
                  Fast forward failed
                </span>
                <span className="text-xs text-red-400/90 leading-tight">
                  Click to retry
                </span>
              </div>
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
            >
              <motion.button
                onClick={handleFastForward}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-1 rounded-full hover:bg-red-500/10 transition-colors"
              >
                <FastForward className="h-5 w-5 text-red-400" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Show idle button (default state) - only if this instance hasn't been executed
  if (shouldShowButton) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="w-full"
      >
        <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
          {/* Enhanced shimmer effect for clickable state */}
          <>
            {/* Outer glow with stronger pulse */}
            <div className="absolute -inset-1 bg-gradient-to-r from-zinc-400/20 via-zinc-300/30 to-zinc-400/20 rounded-lg blur-sm" 
                 style={{ 
                   animation: 'shimmer-pulse 1.2s ease-in-out infinite',
                 }} />
            
            {/* Fast rotating shimmer */}
            <div className="absolute -inset-0.5 rounded-lg overflow-hidden">
              <div className="absolute inset-0 bg-gradient-conic from-transparent via-zinc-400/25 to-transparent animate-spin" 
                   style={{ animationDuration: '0.8s' }} />
            </div>
          </>

          <button 
            onClick={handleFastForward}
            className="relative z-10 flex items-center justify-between px-3 py-2 w-full text-left hover:bg-muted/20 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <MousePointerClick
                className={`w-4 h-4 ${chrome.icon}`}
                strokeWidth={2}
              />
              <div className="flex flex-col">
                <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                  Fast forward price action
                </span>
                <span className={`text-[11px] ${chrome.subtext} truncate`}>
                  Fast forward {args?.time ? `${args.time} hours` : '100 hours'} • Auto-start in {countdown}s
                </span>
              </div>
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
            >
              <FastForward className={`h-5 w-5 ${chrome.iconMuted}`} />
            </motion.div>
          </button>
        </div>
      </motion.div>
    );
  }

  // Fallback - should not reach here
  return null;
}
